import './RunSelectorDropdown.less';

import {type SelectSize} from '@wandb/weave/components/Form/Select';
import React, {forwardRef, useMemo} from 'react';
import {
  type OptionProps,
  type SelectInstance,
  type ValueContainerProps,
} from 'react-select';

import {type RunWithHistoryAndMediaWandb} from '../../MediaCard';
import {
  GenericSelectorDropdown,
  type SelectorOption,
} from '../controls/GenericSelectorDropdown';
import {RunDisplayWithColor} from '../controls/RunDisplayWithColor';

type RunSelectorDropdownProps = {
  size: SelectSize;
  selectedRun: RunWithHistoryAndMediaWandb | null;
  onRunSelect?: (run: RunWithHistoryAndMediaWandb) => void;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
  /** Use card mode (shows title when closed, search when open) instead of standard inline mode */
  useCardMode?: boolean;
  /** Custom title renderer for card mode - if not provided, uses default run display */
  renderTitle?: (run: RunWithHistoryAndMediaWandb | null) => React.ReactNode;
  /** Hide dropdown indicator (chevron icon) - useful for pill variants */
  hideDropdownIndicator?: boolean;
  /** Show clear indicator (x icon to clear selection) - defaults to true in card mode, false in standard mode */
  showClearIndicator?: boolean;
  /** Show color pill instead of colorizing text */
  showColorPill?: boolean;
  /** Label text to display above the dropdown (typically used with card mode) */
  label?: string;
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Maximum width for the menu - useful for constraining standard mode dropdowns */
  maxWidth?: string;
  valueClasses?: string;
  menuItemClasses?: string;
  /** Additional className for the container */
  className?: string;
};

// Custom renderer for run options
const renderRunOption = (
  run: RunWithHistoryAndMediaWandb,
  _props: OptionProps<SelectorOption<RunWithHistoryAndMediaWandb>, false>,
  menuItemClasses?: string,
  showColorPill?: boolean
) => {
  const runSignature = {
    entityName: run.entityName,
    projectName: run.projectName,
    runName: run.name,
  };
  return (
    <RunDisplayWithColor
      displayName={run.displayName ?? run.name}
      disableLink={true}
      color={run.color}
      runSignature={runSignature}
      textClassName={menuItemClasses}
      showColorPill={showColorPill}
      allowWordBreak={true}
    />
  );
};

// Custom renderer for run selected value (standard mode)
const renderRunValue = (
  run: RunWithHistoryAndMediaWandb | null,
  _props: ValueContainerProps<
    SelectorOption<RunWithHistoryAndMediaWandb>,
    false
  >,
  valueClasses?: string,
  showColorPill?: boolean
) => {
  if (!run) {
    return null;
  }
  const runSignature = {
    entityName: run.entityName,
    projectName: run.projectName,
    runName: run.name,
  };
  return (
    <RunDisplayWithColor
      displayName={run.displayName ?? run.name}
      color={run.color}
      runSignature={runSignature}
      disableLink={true}
      textClassName={valueClasses}
      showColorPill={showColorPill}
    />
  );
};

// Default renderer for run title (card mode)
const renderRunTitle = (
  run: RunWithHistoryAndMediaWandb | null,
  showColorPill?: boolean
) => {
  if (!run) {
    return null;
  }
  const runSignature = {
    entityName: run.entityName,
    projectName: run.projectName,
    runName: run.name,
  };
  return (
    <RunDisplayWithColor
      displayName={run.displayName ?? run.name}
      color={run.color}
      runSignature={runSignature}
      disableLink={true}
      showColorPill={showColorPill}
    />
  );
};

export const RunSelectorDropdown = forwardRef<
  SelectInstance<SelectorOption<RunWithHistoryAndMediaWandb>, false>,
  RunSelectorDropdownProps
>(
  (
    {
      size,
      selectedRun,
      onRunSelect,
      runsWithMedia,
      useCardMode = false,
      renderTitle: customRenderTitle,
      hideDropdownIndicator = false,
      showClearIndicator,
      showColorPill = false,
      placeholder = 'Select Run',
      valueClasses,
      menuItemClasses,
      className,
    },
    _ref
  ) => {
    const options: SelectorOption<RunWithHistoryAndMediaWandb>[] = useMemo(
      () =>
        runsWithMedia.map(run => ({
          value: run.name,
          label: run.displayName ?? run.name,
          data: run,
        })),
      [runsWithMedia]
    );

    const selectedOption = useMemo(
      () =>
        selectedRun
          ? (options.find(option => option.value === selectedRun.name) ?? null)
          : null,
      [selectedRun, options]
    );

    const handleSelect = (run: RunWithHistoryAndMediaWandb | null) => {
      if (run && onRunSelect) {
        onRunSelect(run);
      }
    };

    const renderOption = useMemo(
      () =>
        (
          run: RunWithHistoryAndMediaWandb,
          props: OptionProps<SelectorOption<RunWithHistoryAndMediaWandb>, false>
        ) =>
          renderRunOption(run, props, menuItemClasses, showColorPill),
      [menuItemClasses, showColorPill]
    );

    const renderValue = useMemo(
      () =>
        (
          run: RunWithHistoryAndMediaWandb | null,
          props: ValueContainerProps<
            SelectorOption<RunWithHistoryAndMediaWandb>,
            false
          >
        ) =>
          renderRunValue(run, props, valueClasses, showColorPill),
      [valueClasses, showColorPill]
    );

    // Use custom renderTitle if provided, otherwise use default with showColorPill
    const titleRenderer = useMemo(
      () =>
        customRenderTitle
          ? customRenderTitle
          : (run: RunWithHistoryAndMediaWandb | null) =>
              renderRunTitle(run, showColorPill),
      [customRenderTitle, showColorPill]
    );

    // Card mode: use renderTitle, standard mode: use renderValue
    const finalRenderTitle = useCardMode ? titleRenderer : undefined;
    const finalRenderValue = useCardMode ? undefined : renderValue;

    // Default: show clear indicator in card mode, hide in standard mode
    const isClearable = showClearIndicator ?? useCardMode;

    return (
      <GenericSelectorDropdown<RunWithHistoryAndMediaWandb, false>
        size={size}
        selectedValue={selectedOption}
        onSelect={handleSelect}
        options={options}
        isMulti={false}
        isClearable={isClearable}
        className={
          className ??
          `run-selector-dropdown ${
            useCardMode ? '' : 'flex flex-1 items-center justify-start'
          } ${hideDropdownIndicator ? 'bg-transparent' : ''}`
        }
        placeholder={placeholder}
        renderOption={renderOption}
        renderTitle={finalRenderTitle}
        renderValue={finalRenderValue}
        menuItemClasses={menuItemClasses}
        valueClasses={valueClasses}
      />
    );
  }
);

RunSelectorDropdown.displayName = 'RunSelectorDropdown';
